import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  DeleteMenuItemWithConfirmationModal,
  DeleteMenuItemWithConfirmationModalProps,
} from '@/components/poppers/MenuPopper/DeleteMenuItemWithConfirmationModal';
import { useReportError } from '@/hooks/useReportError';
import { useUpdateEntityMutation } from '@/modules/entities/graphql/UpdateEntity.generated';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { getEntitySubtypeUpdateFieldForPolicy } from '@/modules/estate/estateHoldings/estateHoldings.utils';
import { Operations as EstatePageOperations } from '@/modules/estate/graphql/EstatePageData.generated';
import { AugmentedUpdateEntityInput, EntityKind } from '@/types/schema';

interface DeleteInsurancePolicyMenuItemProps
  extends Partial<DeleteMenuItemWithConfirmationModalProps> {
  policyId: string;
  entityId: string;
  entityKind: EntityKind;
  subtypeId: string;
  onDelete: () => void;
  modalProps?: Record<string, unknown>;
  menuItemProps?: Record<string, unknown>;
}

export function DeleteInsurancePolicyMenuItem({
  policyId,
  entityId,
  entityKind,
  subtypeId,
  onDelete,
  modalProps = {},
  menuItemProps = {},
  ...deleteProps
}: DeleteInsurancePolicyMenuItemProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [updateEntity, { loading: deleting }] = useUpdateEntityMutation({
    refetchQueries: [EstatePageOperations.Query.EstatePageData],
    onError: (error) => {
      reportError(`Failed to delete insurance policy`, error);
      showFeedback(
        'Failed to delete this insurance policy. Please refresh the page and try again.'
      );
    },
    onCompleted: () => {
      showFeedback('Insurance policy deleted successfully', {
        variant: 'success',
      });
      onDelete();
    },
  });

  const handleDelete = () => {
    const entityType = getEntityTypeFromEntityKind(entityKind);
    const entitySubtypeUpdateField =
      getEntitySubtypeUpdateFieldForPolicy(entityType);

    if (!entitySubtypeUpdateField) {
      showFeedback(
        'Failed to delete this insurance policy. Unsupported entity type.'
      );
      return;
    }

    const updateEntityInput: AugmentedUpdateEntityInput = {
      id: entityId,
      update: {},
      [entitySubtypeUpdateField]: {
        id: subtypeId,
        update: {
          removePolicyIDs: [policyId],
        },
      },
    };

    updateEntity({
      variables: { input: updateEntityInput },
    });
  };

  return (
    <DeleteMenuItemWithConfirmationModal
      label="Delete policy"
      onConfirmDelete={handleDelete}
      {...deleteProps}
      menuItemProps={{ loading: deleting, ...menuItemProps }}
      modalProps={{
        heading: 'Are you sure you want to delete this insurance policy?',
        ...modalProps,
      }}
    />
  );
}
